
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {NotificationServiceSettings} from './NotificationServiceSettings';
import {FieldModelNotificationServiceSettings} from './NotificationServiceSettings';

/**
 * TradeServerSettings struct
 */
class TradeServerSettings {
  /**
   * Initialize struct
   * @param {!number=} tradePort
   * @param {!boolean=} useSsl
   * @param {!number=} orderTimeout
   * @param {NotificationServiceSettings=} notificationServiceSettings
   * @constructor
   */
  constructor (tradePort = 6140, useSsl = false, orderTimeout = new UInt64(180, 0), notificationServiceSettings = null) {
    this.TradePort = tradePort
    this.UseSsl = useSsl
    this.OrderTimeout = orderTimeout
    this.NotificationServiceSettings = notificationServiceSettings
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TradeServerSettings}
   * @param {!TradeServerSettings} other Other struct
   * @returns {!TradeServerSettings} This struct
   */
  copy (other) {
    if (other.TradePort != null) {
      this.TradePort = other.TradePort
    } else {
      this.TradePort = null
    }
    if (other.UseSsl != null) {
      this.UseSsl = other.UseSsl
    } else {
      this.UseSsl = null
    }
    if (other.OrderTimeout != null) {
      this.OrderTimeout = UInt64.fromNumber(other.OrderTimeout)
    } else {
      this.OrderTimeout = null
    }
    if (other.NotificationServiceSettings != null) {
      this.NotificationServiceSettings = NotificationServiceSettings.fromObject(other.NotificationServiceSettings)
    } else {
      this.NotificationServiceSettings = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TradeServerSettings}
   * @returns {!TradeServerSettings} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TradeServerSettingsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TradeServerSettingsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TradeServerSettings}
   * @param {!TradeServerSettings} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TradeServerSettings)) {
      throw new TypeError('Instance of TradeServerSettings is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TradeServerSettings}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      TradePort: ((this.TradePort != null) ? this.TradePort : null),
      UseSsl: ((this.UseSsl != null) ? this.UseSsl : null),
      OrderTimeout: ((this.OrderTimeout != null) ? this.OrderTimeout.toNumber() : null),
      NotificationServiceSettings: ((this.NotificationServiceSettings != null) ? this.NotificationServiceSettings : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TradeServerSettings.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TradeServerSettings} other Object value
   * @returns {!TradeServerSettings} Created struct
   */
  static fromObject (other) {
    return new TradeServerSettings().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TradeServerSettings}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TradeServerSettings.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TradeServerSettings}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 12
  }
}

export { TradeServerSettings };

/**
 * Fast Binary Encoding TradeServerSettings field model
 */
class FieldModelTradeServerSettings extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.TradePort = new fbe.FieldModelUInt16(buffer, 4 + 4)
    this.UseSsl = new fbe.FieldModelBool(buffer, this.TradePort.fbeOffset + this.TradePort.fbeSize)
    this.OrderTimeout = new fbe.FieldModelUInt64(buffer, this.UseSsl.fbeOffset + this.UseSsl.fbeSize)
    this.NotificationServiceSettings = new fbe.FieldModelOptional(new FieldModelNotificationServiceSettings(buffer, this.OrderTimeout.fbeOffset + this.OrderTimeout.fbeSize), buffer, this.OrderTimeout.fbeOffset + this.OrderTimeout.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelTradeServerSettings}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTradeServerSettings}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.TradePort.fbeSize + this.UseSsl.fbeSize + this.OrderTimeout.fbeSize + this.NotificationServiceSettings.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTradeServerSettings}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.TradePort.fbeExtra + this.UseSsl.fbeExtra + this.OrderTimeout.fbeExtra + this.NotificationServiceSettings.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTradeServerSettings}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTradeServerSettings.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTradeServerSettings}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 12
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTradeServerSettings}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTradeServerSettings}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.TradePort.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TradePort.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TradePort.fbeSize

    if ((fbeCurrentSize + this.UseSsl.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.UseSsl.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSsl.fbeSize

    if ((fbeCurrentSize + this.OrderTimeout.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OrderTimeout.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderTimeout.fbeSize

    if ((fbeCurrentSize + this.NotificationServiceSettings.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NotificationServiceSettings.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NotificationServiceSettings.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTradeServerSettings}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTradeServerSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTradeServerSettings}
   * @param {!TradeServerSettings} fbeValue Default value, defaults is new TradeServerSettings()
   * @returns {!TradeServerSettings} TradeServerSettings value
   */
  get (fbeValue = new TradeServerSettings()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTradeServerSettings}
   * @param {!TradeServerSettings} fbeValue TradeServerSettings value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.TradePort.fbeSize) <= fbeStructSize) {
      fbeValue.TradePort = this.TradePort.get(6140)
    } else {
      fbeValue.TradePort = 6140
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TradePort.fbeSize

    if ((fbeCurrentSize + this.UseSsl.fbeSize) <= fbeStructSize) {
      fbeValue.UseSsl = this.UseSsl.get(false)
    } else {
      fbeValue.UseSsl = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.UseSsl.fbeSize

    if ((fbeCurrentSize + this.OrderTimeout.fbeSize) <= fbeStructSize) {
      fbeValue.OrderTimeout = this.OrderTimeout.get(new UInt64(180, 0))
    } else {
      fbeValue.OrderTimeout = new UInt64(180, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderTimeout.fbeSize

    if ((fbeCurrentSize + this.NotificationServiceSettings.fbeSize) <= fbeStructSize) {
      fbeValue.NotificationServiceSettings = this.NotificationServiceSettings.get()
    } else {
      fbeValue.NotificationServiceSettings = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NotificationServiceSettings.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTradeServerSettings}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTradeServerSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTradeServerSettings}
   * @param {!TradeServerSettings} fbeValue TradeServerSettings value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTradeServerSettings}
   * @param {TradeServerSettings} fbeValue TradeServerSettings value
   */
  setFields (fbeValue) {
    this.TradePort.set(fbeValue.TradePort)
    this.UseSsl.set(fbeValue.UseSsl)
    this.OrderTimeout.set(fbeValue.OrderTimeout)
    this.NotificationServiceSettings.set(fbeValue.NotificationServiceSettings)
  }
}

export { FieldModelTradeServerSettings };

/**
 * Fast Binary Encoding TradeServerSettings model
 */
class TradeServerSettingsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTradeServerSettings(this.buffer, 4)
  }

  /**
   * Get the TradeServerSettings model
   * @this {!TradeServerSettingsModel}
   * @returns {!FieldModelTradeServerSettings} model TradeServerSettings model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TradeServerSettingsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TradeServerSettingsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TradeServerSettingsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TradeServerSettingsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTradeServerSettings.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TradeServerSettingsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TradeServerSettingsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TradeServerSettingsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TradeServerSettingsModel}
   * @param {!TradeServerSettings} value TradeServerSettings value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TradeServerSettingsModel}
   * @param {!TradeServerSettings} value TradeServerSettings value, defaults is new TradeServerSettings()
   * @return {!object} Deserialized TradeServerSettings value and its size
   */
  deserialize (value = new TradeServerSettings()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TradeServerSettings(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TradeServerSettings(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TradeServerSettingsModel}
   * @param {!number} prev Previous TradeServerSettings model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TradeServerSettingsModel };
